/**
 * /field/ndt — an NDT technician's own jobs (GET
 * /ndt-inspection/field/my-jobs, fieldiq-api#1144).
 *
 * THE SELECTION IS THE API'S: the jobs whose crew the caller is on, still
 * scheduled or active. This screen only splits them under two headings, in
 * the API's order. The field surface is SNAKE_CASE (job_number, weld_counts),
 * unlike the office NDT reads.
 *
 * The frame is Pressure Testing's field frame (PtFieldPage), imported, so the
 * verticals' field screens stay one shape: the global header, a back arrow,
 * no bottom bar, every action a full-width button in the flow.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin } from 'lucide-react';
import { useNdtFieldJobs } from '../../../hooks/useNdtInspection';
import { formatCalendarDate } from '../../../lib/calendar-date';
import { ndtJobStatusSkin } from '../../../lib/ndt-inspection';
import type { NdtFieldJob } from '../../../types/ndt-inspection';
import { PtFieldBadge, PtFieldCard, PtFieldEmpty, PtFieldPage, PtFieldQueryState, PtFieldSectionTitle } from '../../pressure-testing/field/PtFieldBits';

/** Location, then LSD, then line. None → `—`. */
export function ndtFieldJobWhere(j: Pick<NdtFieldJob, 'location_name' | 'lsd' | 'line_ref'>): string {
  const parts = [j.location_name, j.lsd ? `LSD ${j.lsd}` : null].filter(Boolean);
  return parts.length ? parts.join(' · ') : j.line_ref ? `Line ${j.line_ref}` : '—';
}

/** "3 of 8 welds accepted" from the API's counts; none → "No welds yet". */
export function ndtFieldWeldProgress(c: NdtFieldJob['weld_counts'] | null | undefined): string {
  if (!c || typeof c.total !== 'number') return '—';
  if (c.total === 0) return 'No welds yet';
  return `${c.accept} of ${c.total} welds accepted`;
}

function JobCard({ job }: { job: NdtFieldJob }) {
  return (
    <Link to={`/field/ndt/jobs/${job.id}`} className="block" data-testid="ndt-my-job">
      <PtFieldCard className="flex min-h-[88px] items-center gap-3 px-4 py-4 active:bg-fieldiq-dark/50">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-base font-extrabold text-fieldiq-text">{job.job_number || '—'}</span>
            <PtFieldBadge skin={ndtJobStatusSkin(job.status)} />
          </div>
          <p className="mt-1 truncate text-base font-bold text-white">{job.customer_name || '—'}</p>
          <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-fieldiq-text">
            <MapPin className="size-3.5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
            {ndtFieldJobWhere(job)}
          </p>
          <p className="mt-1 text-xs text-fieldiq-muted">
            {ndtFieldWeldProgress(job.weld_counts)}
            {job.status === 'scheduled' && <> · {formatCalendarDate(job.scheduled_date, { weekday: 'short', month: 'short', day: 'numeric' }) ?? 'Not dated'}</>}
          </p>
        </div>
        <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />
      </PtFieldCard>
    </Link>
  );
}

export default function NdtMyJobsScreen() {
  const query = useNdtFieldJobs();
  const jobs = query.data?.data ?? [];
  const groups = [
    { key: 'active', title: 'Active', jobs: jobs.filter((j) => j.status === 'active') },
    { key: 'scheduled', title: 'Scheduled', jobs: jobs.filter((j) => j.status === 'scheduled') },
  ].filter((g) => g.jobs.length > 0);

  return (
    <PtFieldPage title="My NDT Jobs" backTo="/field" testId="ndt-my-jobs">
      <PtFieldQueryState query={query} subject="your NDT jobs">
        {groups.length === 0 ? (
          <PtFieldEmpty>You are not on the crew of any open NDT job. The office puts you on a job&apos;s crew.</PtFieldEmpty>
        ) : (
          groups.map((g) => (
            <section key={g.key} aria-labelledby={`ndt-group-${g.key}`}>
              <PtFieldSectionTitle>
                <span id={`ndt-group-${g.key}`}>
                  {g.title} · {g.jobs.length}
                </span>
              </PtFieldSectionTitle>
              <div className="space-y-2">
                {g.jobs.map((j) => (
                  <JobCard key={j.id} job={j} />
                ))}
              </div>
            </section>
          ))
        )}
      </PtFieldQueryState>
    </PtFieldPage>
  );
}
